/**
 * API Support helper methods for LoaderPreProcess (private methods only)
 * The public getTemplatesApiResponse method is in loaderPreProcess_ILoader.ts
 */

import { JsonUtility } from '../../app/jsonUtility.js';
import { JsonObject } from '../../app/json/jsonObject.js';
import { AppSiteTemplateData } from '../../model/appSiteTemplateData.js';
import { SerializePreProcessMetadata } from './serializePreProcessMetadata.js';
import type { LoaderPreProcess } from './loaderPreProcess.js';
import type { PreprocessedTemplate } from '../../model/index.js';

type LoaderPreProcessApiSerializerHost = {
    _escapeJsonString(value: string | null | undefined): string;
    _serializeSlotsArray(slots: PreprocessedTemplate['slottedTemplates'][number]['slots']): string;
    _serializeSlottedTemplatesArray(slottedTemplates: PreprocessedTemplate['slottedTemplates']): string;
};



function serializeSlottedTemplate(loader: LoaderPreProcessApiSerializerHost, slotted: PreprocessedTemplate['slottedTemplates'][number]): string {
    const parts: string[] = [];
    parts.push('{"name":"');
    parts.push(loader._escapeJsonString(slotted.name));
    parts.push('","fullMatch":"');
    parts.push(loader._escapeJsonString(slotted.fullMatch || ''));
    parts.push('","slots":');
    parts.push(loader._serializeSlotsArray(slotted.slots || []));
    parts.push('}');
    return parts.join('');
}


/**
 * API Support helper methods for LoaderPreProcess (private methods only)
 * The public getTemplatesApiResponse method is in loaderPreProcess_ILoader.ts
 */
export const apiMethods = {
    /**
     * Gets all templates as dictionary of AppSite to template data
     */
    _getAllTemplatesJson(this: LoaderPreProcess, includeSearchAppSites: boolean = true, orderedTemplateNames: string[] | null = null): Record<string, AppSiteTemplateData> {
        const result: Record<string, AppSiteTemplateData> = {};

        // Build list of AppSites to include (main first, then SearchAppSites)
        const searchAppSites = includeSearchAppSites ? this.getSearchAppSitesForAppSite(this._appSite) : '';
        const appSitesToInclude = (this.constructor as typeof LoaderPreProcess).BuildAppSitesToLoad(this._appSite, searchAppSites);

        // Build AppSiteTemplateData for each AppSite in order
        for (const appSite of appSitesToInclude) {
            if (!this._appSiteCache.has(appSite)) {
                continue;
            }
            const templatesJson = this._buildTemplatesJsonForAppSite(appSite, orderedTemplateNames);

            result[appSite] = new AppSiteTemplateData(appSite, templatesJson, null, this.getAppFile(appSite, '').appFile);
        }

        return result;
    },

    /**
     * Builds templates JSON for a single AppSite
     * When orderedTemplateNames is provided, templates are ordered according to that list (processing order)
     */
    _buildTemplatesJsonForAppSite(this: LoaderPreProcess, appSite: string, orderedTemplateNames: string[] | null = null): string {
        const cached = this._appSiteCache.get(appSite);
        if (!cached) return '{}';

        const parts: string[] = [];
        parts.push('{');

        // Build list of template keys in correct order
        let templateKeys: string[];
        const resolvedNames = new Map<string, string>();
        if (orderedTemplateNames && orderedTemplateNames.length > 0) {
            // orderedTemplateNames is the SET as well as the order: it is the engine's merge closure.
            templateKeys = [];
            const addedKeys = new Set<string>();
            const allKeys = Array.from(cached.templates.keys());

            for (const name of orderedTemplateNames) {
                const componentName = name.startsWith('!') ? name.slice(1) : name;
                const componentLower = componentName.toLowerCase();
                for (const key of allKeys) {
                    if (addedKeys.has(key)) continue;
                    const keyLower = key.toLowerCase();
                    if (keyLower === componentLower || keyLower.endsWith(`_${componentLower}`)) {
                        templateKeys.push(key);
                        addedKeys.add(key);
                        const appSitePrefix = `${appSite.toLowerCase()}_`;
                        resolvedNames.set(key, componentLower.startsWith(appSitePrefix)
                            ? componentLower.slice(appSitePrefix.length)
                            : componentLower);
                        break;
                    }
                }
            }
        } else {
            // Use natural insertion order - templates are inserted in consistent order during preprocessing
            templateKeys = Array.from(cached.templates.keys());
        }

        let first = true;
        for (const templateKey of templateKeys) {
            const entry = cached.templates.get(templateKey)!;
            const { template } = entry;
            let json: JsonObject | null = entry.json;

            const resolvedName = resolvedNames.get(templateKey);
            if (resolvedName !== undefined) {
                // The closure merge has already run every server-owned handler. Ship that exact resolved
                // DataJson to the client tier; serializing the raw disk JSON here would reintroduce
                // placeholders such as {{DynamicData}}, which the browser cannot and must not execute
                // server-side. resolvedTemplateJson is keyed "{mergeAppSite}_{template}" lower-cased
                // (loaderPreProcess_Inheritance takes the MERGE appSite, the owning one arriving separately
                // as resolvedAppSite), so a template pulled in from another AppSite by a gated redirect is
                // stored under the merge AppSite -- recompose against this._appSite, not against appSite.
                const resolvedJson = this._dynamicResults.resolvedTemplateJson
                    .get(`${this._appSite.toLowerCase()}_${resolvedName}`).asObject();
                if (resolvedJson) {
                    json = resolvedJson;
                }
            }

            if (!first) parts.push(',');
            first = false;

            parts.push('"');
            parts.push((this.constructor as typeof LoaderPreProcess)._escapeJsonString(templateKey));
            parts.push('":{"Preprocessed":');

            // Emit the resolved PreprocessedTemplate as an OPAQUE artifact string (the complete capitalized
            // shape via the loader-owned serializer — byte-identical to the OCI ".ociartifacts/preprocessed/*"
            // sidecar). ConfigClient stores it verbatim and the loader rehydrates it via
            // DeserializePreProcessMetadata, so PreProcess client mode reuses the server-computed structure
            // instead of re-parsing raw HTML (closing the originalContent leak).
            parts.push('"');
            parts.push((this.constructor as typeof LoaderPreProcess)._escapeJsonString(SerializePreProcessMetadata.serializeTemplate(template)));
            parts.push('"');

            // DataJson alongside (the engine's content payload), mirroring the NormalJson/PreProcessJson
            // {"HtmlJson":..,"DataJson":..} api shape.
            parts.push(',"DataJson":');
            if (!json) {
                parts.push('null');
            } else {
                parts.push(JsonUtility.serializeObject(json));
            }

            parts.push('}');
        }

        parts.push('}');
        return parts.join('');
    },

    /**
     * Masks originalText value: keeps placeholders like {{#X}}...{{/X}}, replaces inner HTML with [HTML]
     * Used to strip raw HTML from API responses while preserving template structure
     */
    _maskOriginalText(this: LoaderPreProcess, value: string): string {
        if (!value) return value;

        // If no HTML tags (no '<'), keep the entire value as is (it's all placeholders)
        if (value.indexOf('<') < 0) {
            return value;
        }

        // Check for section markers like {{#Name}}...{{/Name}} or {{$Name}}...{{/$Name}}
        const openingStart = value.indexOf('{{');
        if (openingStart >= 0) {
            const openingEnd = value.indexOf('}}', openingStart);
            if (openingEnd >= 0) {
                const openingTag = value.substring(openingStart, openingEnd + 2);

                // Check if it's a section tag ({{#...}} or {{$...}})
                if (openingTag.length > 4 && (openingTag[2] === '#' || openingTag[2] === '$')) {
                    // Extract the tag name
                    const tagName = openingTag.substring(3, openingTag.length - 2);

                    // Find closing tag {{/Name}}
                    const closingTag = '{{/' + tagName + '}}';
                    const closingIndex = value.lastIndexOf(closingTag);

                    if (closingIndex > openingEnd) {
                        // Keep opening tag, replace inner content with [HTML], keep closing tag
                        return openingTag + '[HTML]' + closingTag;
                    }
                }
            }
        }

        // Has HTML but no section markers - replace entire thing with [HTML]
        return '[HTML]';
    }
};

/**
 * Static methods to be attached to LoaderPreProcess class
 */
export const apiStaticMethods = {
    /**
     * Escapes a string for safe JSON output
     */
    _escapeJsonString(value: string | null | undefined): string {
        if (value === null || value === undefined) return '';
        const str = String(value);
        let result = '';
        for (let i = 0; i < str.length; i++) {
            const ch = str[i];
            switch (ch) {
                case '"': result += '\\u0022'; break;
                case '\\': result += '\\\\'; break;
                case '\b': result += '\\b'; break;
                case '\f': result += '\\f'; break;
                case '\n': result += '\\n'; break;
                case '\r': result += '\\r'; break;
                case '\t': result += '\\t'; break;
                default: {
                    const code = ch.charCodeAt(0);
                    if (code < 32) {
                        result += '\\u' + code.toString(16).padStart(4, '0');
                    } else {
                        result += ch;
                    }
                }
            }
        }
        return result;
    },

    /**
     * Serializes slottedTemplates array
     */
    _serializeSlottedTemplatesArray(slottedTemplates: PreprocessedTemplate['slottedTemplates']): string {
        const parts: string[] = [];
        parts.push('[');
        let first = true;
        for (const slotted of slottedTemplates) {
            if (!first) parts.push(',');
            first = false;
            parts.push(this.serializeSlottedTemplate(slotted));
        }
        parts.push(']');
        return parts.join('');
    },

    serializeSlotPlaceholder(this: LoaderPreProcessApiSerializerHost, slot: PreprocessedTemplate['slottedTemplates'][number]['slots'][number]): string {
        const parts: string[] = [];
        parts.push('{"slotKey":"');
        parts.push(this._escapeJsonString(slot.slotKey));
        parts.push('","content":"');
        parts.push(this._escapeJsonString(slot.content || ''));
        parts.push('","nestedPlaceholders":[');
        let firstPlaceholder = true;
        for (const placeholder of slot.nestedPlaceholders || []) {
            if (!firstPlaceholder) parts.push(',');
            firstPlaceholder = false;
            parts.push('{"name":"');
            parts.push(this._escapeJsonString(placeholder.name));
            parts.push('"}');
        }
        parts.push('],"nestedSlottedTemplates":');
        parts.push(this._serializeSlottedTemplatesArray(slot.nestedSlottedTemplates || []));
        parts.push('}');
        return parts.join('');
    },

    serializeSlottedTemplate(this: LoaderPreProcessApiSerializerHost, slotted: PreprocessedTemplate['slottedTemplates'][number]): string {
        return serializeSlottedTemplate(this, slotted);
    },

    /**
     * Serializes slots array
     */
    _serializeSlotsArray(slots: PreprocessedTemplate['slottedTemplates'][number]['slots']): string {
        const parts: string[] = [];
        parts.push('[');
        let first = true;
        for (const slot of slots) {
            if (!first) parts.push(',');
            first = false;
            parts.push(this.serializeSlotPlaceholder(slot));
        }
        parts.push(']');
        return parts.join('');
    }
};
